/*global globalThis*/
import {
  fromPairs,
  invertObj,
  map,
  mapObjIndexed,
  mergeAll,
  pathOr,
  pipe,
  reverse,
  values,
} from 'ramda';
import { call } from 'redux-saga/effects';
import { matchPath } from 'react-router-dom';
import { NetworkError } from '@corefront/eva-fe-sdk';
import { sendErrorAnalyticsData } from '../services/analytics';

export const sortBySortOverride = (list, overrideList = []) => {
  const hashMap = pipe(reverse, invertObj)(overrideList);
  return [...list].sort(
    (a, b) => pathOr(-1, [b.id], hashMap) - pathOr(-1, [a.id], hashMap)
  );
};

export const getWindowLocationHref = () => window.location.href;

export const getLobbyURL = (baseUrl, lang) => {
  const baseUrlWithCurrentLang = baseUrl.replace(/^\/(\w{2})\//, `/${lang}/`);
  return `${globalThis.location.origin}${baseUrlWithCurrentLang}`;
};

export const getCasinoLobbyURLFromHref = (href) => {
  const casinoBaseRegExp = /(https?:\/\/.+?\/(.+?)?pm-casino\/).*/;
  return href.replace(casinoBaseRegExp, '$1');
};

/**
 * Custom Redux-Saga effect
 * @link https://github.com/redux-saga/redux-saga/issues/1045
 * @param SomeClass - any <class> that should be instantiated.
 * @param args - arguments for <class> constructor
 * @return - instance of a <class>
 */
export const create = (SomeClass, ...args) =>
  call(() => new SomeClass(...args));

/**
 * Check, if pathname
 * @link https://github.com/ReactTraining/react-router/blob/master/packages/react-router/modules/matchPath.js#L28
 * @param args
 * @param args.pathname - Current pathname from location
 * @param args.path - react-router path as regexp
 * @returns {boolean} - matches or not
 */
export const isPathMatches = (...args) => !!matchPath.apply(null, args);

/**
 * @param {string} pathname - current pathname from location
 * @param {Array.<string>} routes - list of available routes
 * @return {(string|null)}
 */
export const getPageRoute = (pathname, routes) =>
  routes.find((route) =>
    isPathMatches(pathname, { path: route, exact: true })
  ) || null;

/**
 * Returns array of random items of length "count" from "collection"
 * @param {Array} array
 * @return {[]}
 */
export const shuffle = (array) => [...array].sort(() => Math.random() - 0.5);

/**
 * @param {number} val
 * @return {boolean}
 */
export const isOdd = (val) => val % 2 !== 0;

/**
 * @param {number} length
 * @return {number[]}
 */
export const createMockArray = (length) => [...new Array(length).keys()];

export function numberWithSpaces(number) {
  if (typeof number === 'number') {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' '); // solution from https://stackoverflow.com/questions/16637051/adding-space-between-numbers
  }

  return number;
}

/**
 * Creates mapped object with equal values
 * @param {Array<string|number>} channels - array of keys in map
 * @param {any} value - value for each key in map
 */
export const createMapping = (channels, value) =>
  fromPairs(map((channel) => [channel, value], channels));

/**
 * Creates plain mapped config from such structure:
 * { value1: ['key1', 'key2'], value2: ['key3', 'key4'] }
 * Results:
 * { 'key1': value1, 'key2': value1, 'key3': value2, 'key4': value2 }
 * @param mapConfig
 */
export const createConfigMap = pipe(
  mapObjIndexed(createMapping),
  values,
  mergeAll
);

/**
 * Capitalize first letter in string
 * @param {string} s
 * @returns {string}
 */
export const capitalize = (s) => {
  if (typeof s !== 'string') return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
};

export const COINS_COUNT = 100;
export const convertCoinsToAmount = (coins) => {
  return typeof coins === 'number' ? coins / COINS_COUNT : coins;
};

/**
 * function should return value like '12 345 678,92'
 * @param {number | undefined} amount
 * @param {number} fractionDigits
 * @returns {string}
 */
export const formatToDisplay = (amount, fractionDigits = 2) => {
  return (
    amount?.toLocaleString(['ru-RU'], {
      maximumFractionDigits: fractionDigits,
      minimumFractionDigits: fractionDigits,
    }) ?? ''
  );
};

const DEFAULT_CHUNK_SIZE = 2;

/**
 * Func for splitting string like "AAUSBYUA"
 * to ["AA", "US", "BY", "UA"]
 * @param {string} str
 * @param {number} chunkSize
 * @returns {null | Array} splited string on countries
 */
export const getChunksFromString = (str, chunkSize = DEFAULT_CHUNK_SIZE) => {
  const regexChunk = new RegExp(`.{1,${chunkSize}}`, 'g');
  return str?.match(regexChunk);
};

export const buildErrorAnalyticsData = (context, error) => {
  return {
    error_code: error?.statusCode ? `${error?.statusCode}` : null,
    error_info: JSON.stringify({
      error: error?.toString?.(),
      context: {
        ...context,
        headers: {
          ...(context?.headers ?? {}),
          Authorization: context?.headers?.Authorization ? true : false,
        },
      },
      stack: JSON.stringify(error?.stack).substring(0, 512),
    }).substring(0, 2047), // max length is 2048
  };
};

export const sdkErrorHandler = (context) => (e) => {
  sendErrorAnalyticsData(buildErrorAnalyticsData(context, e));

  if (e instanceof NetworkError) {
    console.log('NetworkError', e);
    throw e;
  } else {
    console.log('SDK-Error', e);
  }
};
